import { getPermissionKeyForPath } from '@/application/data/sidebar';

export type PermissionAction = 'read' | 'create' | 'update' | 'delete';

export interface UserProfileLike {
  user_type?: string;
  role?: string;
  roles?: string[];
}

export const isBusinessUserProfile = (userProfile: UserProfileLike | null) => {
  const userType = userProfile?.user_type?.toLowerCase();
  const userRole = userProfile?.role?.toLowerCase();
  const userRoles = (userProfile?.roles ?? []).map((role) =>
    role.toLowerCase()
  );

  return (
    userType === 'business' ||
    userRole === 'business' ||
    userRoles.includes('business')
  );
};

export const evaluatePermissionExpression = (
  requiredPermission: string,
  hasPermission: (permissionKey: string) => boolean,
  action: PermissionAction = 'read'
): boolean => {
  const checkPermission = (key: string) =>
    hasPermission(`${key}.${action}`) || hasPermission(key);

  if (requiredPermission.includes('|')) {
    return requiredPermission
      .split('|')
      .map((permission) => permission.trim())
      .some((permission) => checkPermission(permission));
  }

  return requiredPermission
    .split(',')
    .map((permission) => permission.trim())
    .every((permission) => checkPermission(permission));
};

type CanAccessPathArgs = Readonly<{
  pathname: string;
  userProfile: UserProfileLike | null;
  hasPermission: (permissionKey: string) => boolean;
  action?: PermissionAction;
  fallbackIfNoPermissionKey?: boolean;
}>;

/**
 * Centralized path access evaluation used by route guards, layout, and hooks.
 * `fallbackIfNoPermissionKey` controls fail-closed vs fail-open behavior.
 */
export const canAccessPath = ({
  pathname,
  userProfile,
  hasPermission,
  action = 'read',
  fallbackIfNoPermissionKey = false,
}: CanAccessPathArgs): boolean => {
  const isBusiness = isBusinessUserProfile(userProfile);
  const requiredPermission = getPermissionKeyForPath(pathname, isBusiness);
  if (!requiredPermission) {
    return fallbackIfNoPermissionKey;
  }
  return evaluatePermissionExpression(
    requiredPermission,
    hasPermission,
    action
  );
};
